/**
 * formula.js — tiny, safe spreadsheet formula engine for table blocks.
 *
 * No eval(). A hand-written tokenizer + recursive-descent parser evaluates:
 *   • numbers, strings ("…"), cell refs (A1), ranges (A1:B5 — inside functions)
 *   • operators:  + - * / ^ %   comparisons:  = <> < <= > >=
 *   • functions:  SUM AVG/AVERAGE MIN MAX COUNT ROUND ABS IF
 *
 * Errors are honest, never fabricated values:
 *   #REF! (bad reference) · #CYCLE! (circular) · #DIV/0! · #ERROR! (syntax)
 */

const FN = {
  SUM:     (xs) => xs.reduce((a, b) => a + b, 0),
  AVG:     (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0),
  AVERAGE: (xs) => FN.AVG(xs),
  MIN:     (xs) => (xs.length ? Math.min(...xs) : 0),
  MAX:     (xs) => (xs.length ? Math.max(...xs) : 0),
  COUNT:   (xs) => xs.length,
  ABS:     (xs) => Math.abs(xs[0] ?? 0),
  ROUND:   (xs) => {
    const m = Math.pow(10, Math.trunc(xs[1] ?? 0))
    return Math.round((xs[0] ?? 0) * m) / m
  },
}

const CELL_RE = /^([A-Z]{1,2})([0-9]{1,3})$/

export function colLabel(i) {
  // 0 → A, 25 → Z, 26 → AA
  let s = ''
  i += 1
  while (i > 0) {
    const r = (i - 1) % 26
    s = String.fromCharCode(65 + r) + s
    i = Math.floor((i - 1) / 26)
  }
  return s
}

function refToRC(ref) {
  const m = CELL_RE.exec(ref.toUpperCase())
  if (!m) return null
  let col = 0
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64)
  return { r: parseInt(m[2], 10) - 1, c: col - 1 }
}

// ── Tokenizer ────────────────────────────────────────────────────────────────

function tokenize(src) {
  const toks = []
  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (ch === ' ' || ch === '\t') { i++; continue }
    if (/[0-9.]/.test(ch)) {
      let j = i
      while (j < src.length && /[0-9.]/.test(src[j])) j++
      const num = parseFloat(src.slice(i, j))
      if (Number.isNaN(num)) throw new Error('bad number')
      toks.push({ t: 'num', v: num }); i = j; continue
    }
    if (/[A-Za-z]/.test(ch)) {
      let j = i
      while (j < src.length && /[A-Za-z0-9]/.test(src[j])) j++
      toks.push({ t: 'ident', v: src.slice(i, j).toUpperCase() }); i = j; continue
    }
    if (ch === '"') {
      let j = i + 1
      while (j < src.length && src[j] !== '"') j++
      if (j >= src.length) throw new Error('unterminated string')
      toks.push({ t: 'str', v: src.slice(i + 1, j) }); i = j + 1; continue
    }
    if (ch === '<' && src[i + 1] === '=') { toks.push({ t: 'op', v: '<=' }); i += 2; continue }
    if (ch === '>' && src[i + 1] === '=') { toks.push({ t: 'op', v: '>=' }); i += 2; continue }
    if (ch === '<' && src[i + 1] === '>') { toks.push({ t: 'op', v: '<>' }); i += 2; continue }
    if ('+-*/^%<>=(),:'.includes(ch)) { toks.push({ t: 'op', v: ch }); i++; continue }
    throw new Error(`unexpected '${ch}'`)
  }
  return toks
}

// ── Parser / evaluator ───────────────────────────────────────────────────────

function makeParser(toks, getCell) {
  let pos = 0
  const peek = () => toks[pos]
  const eat = (v) => {
    const t = toks[pos]
    if (!t || (v !== undefined && t.v !== v)) throw new Error(`expected ${v}`)
    pos++
    return t
  }

  function collectRangeValues(a, b) {
    const A = refToRC(a), B = refToRC(b)
    if (!A || !B) throw Object.assign(new Error('bad ref'), { ref: true })
    const out = []
    for (let r = Math.min(A.r, B.r); r <= Math.max(A.r, B.r); r++) {
      for (let c = Math.min(A.c, B.c); c <= Math.max(A.c, B.c); c++) {
        const v = getCell(r, c)
        if (typeof v === 'number') out.push(v)
      }
    }
    return out
  }

  function primary() {
    const t = peek()
    if (!t) throw new Error('unexpected end')
    if (t.t === 'num') { pos++; return t.v }
    if (t.t === 'str') { pos++; return t.v }
    if (t.v === '(') { eat('('); const v = comparison(); eat(')'); return v }
    if (t.v === '-') { eat('-'); return -primary() }
    if (t.v === '+') { eat('+'); return primary() }
    if (t.t === 'ident') {
      pos++
      // Function call (IF is special-cased below — it keeps strings/booleans)
      if (peek()?.v === '(' && (FN[t.v] || t.v === 'IF')) {
        eat('(')
        const args = []
        if (peek()?.v !== ')') {
          for (;;) {
            // range argument?
            const save = pos
            const tk = peek()
            if (tk?.t === 'ident' && CELL_RE.test(tk.v)) {
              pos++
              if (peek()?.v === ':') {
                eat(':')
                const end = eat()
                if (end.t !== 'ident' || !CELL_RE.test(end.v)) throw new Error('bad range')
                args.push(...collectRangeValues(tk.v, end.v))
                if (peek()?.v === ',') { eat(','); continue }
                break
              }
              pos = save   // single ref — re-parse as expression
            }
            const v = comparison()
            args.push(typeof v === 'boolean' ? (v ? 1 : 0) : v)
            if (peek()?.v === ',') { eat(','); continue }
            break
          }
        }
        eat(')')
        const nums = args.filter((x) => typeof x === 'number')
        if (t.v === 'IF') {
          const [cond, yes, no] = args
          return (typeof cond === 'number' ? cond !== 0 : !!cond) ? (yes ?? 0) : (no ?? 0)
        }
        return FN[t.v](nums)
      }
      // Unknown function name → syntax error, not a reference error
      if (peek()?.v === '(') throw new Error(`unknown function ${t.v}`)
      // Cell reference
      const rc = refToRC(t.v)
      if (!rc) throw Object.assign(new Error(`unknown name ${t.v}`), { ref: true })
      const v = getCell(rc.r, rc.c)
      if (v && typeof v === 'object' && v.error) throw Object.assign(new Error(v.error), { propagated: v.error })
      return typeof v === 'number' ? v : (v ?? 0)
    }
    throw new Error(`unexpected ${t.v}`)
  }

  function power() {
    let v = primary()
    while (peek()?.v === '^') { eat('^'); v = Math.pow(v, primary()) }
    return v
  }

  function term() {
    let v = power()
    for (;;) {
      const t = peek()
      if (t?.v === '*') { eat('*'); v = v * power() }
      else if (t?.v === '/') {
        eat('/')
        const d = power()
        if (d === 0) throw Object.assign(new Error('division by zero'), { div0: true })
        v = v / d
      }
      else if (t?.v === '%') { eat('%'); v = v / 100 }
      else break
    }
    return v
  }

  function sum() {
    let v = term()
    for (;;) {
      const t = peek()
      if (t?.v === '+') { eat('+'); v = v + term() }
      else if (t?.v === '-') { eat('-'); v = v - term() }
      else break
    }
    return v
  }

  function comparison() {
    let v = sum()
    const t = peek()
    if (t?.t === 'op' && ['=', '<>', '<', '<=', '>', '>='].includes(t.v)) {
      eat(t.v)
      const rhs = sum()
      switch (t.v) {
        case '=':  return v === rhs
        case '<>': return v !== rhs
        case '<':  return v < rhs
        case '<=': return v <= rhs
        case '>':  return v > rhs
        case '>=': return v >= rhs
      }
    }
    return v
  }

  return () => {
    const v = comparison()
    if (pos !== toks.length) throw new Error('trailing input')
    return v
  }
}

// ── Public: evaluate an entire grid ──────────────────────────────────────────

/**
 * rows: string[][] of raw cell text. Returns { values, display } where
 *   values[r][c]  — number | string | { error }
 *   display[r][c] — the string to render
 */
export function evaluateTable(rows) {
  const memo = new Map()       // "r,c" → value
  const visiting = new Set()   // cycle detection

  function cellValue(r, c) {
    const key = `${r},${c}`
    if (memo.has(key)) return memo.get(key)
    const raw = (rows[r] && rows[r][c] != null) ? String(rows[r][c]).trim() : ''
    let out
    if (raw === '') out = null
    else if (raw.startsWith('=')) {
      if (visiting.has(key)) { out = { error: '#CYCLE!' } }
      else {
        visiting.add(key)
        try {
          const v = makeParser(tokenize(raw.slice(1)), cellValue)()
          out = typeof v === 'boolean' ? (v ? 1 : 0) : v
          if (typeof out === 'number' && !Number.isFinite(out)) out = { error: '#ERROR!' }
        } catch (err) {
          if (err.propagated)  out = { error: err.propagated }
          else if (err.div0)   out = { error: '#DIV/0!' }
          else if (err.ref)    out = { error: '#REF!' }
          else                 out = { error: '#ERROR!' }
        }
        visiting.delete(key)
      }
    } else {
      const n = Number(raw.replace(/,/g, ''))
      out = raw !== '' && !Number.isNaN(n) ? n : raw
    }
    memo.set(key, out)
    return out
  }

  const nRows = rows.length
  const nCols = Math.max(0, ...rows.map((r) => r.length))
  const values = [], display = []
  for (let r = 0; r < nRows; r++) {
    values.push([]); display.push([])
    for (let c = 0; c < nCols; c++) {
      const v = cellValue(r, c)
      values[r][c] = v
      display[r][c] =
        v == null ? '' :
        typeof v === 'object' ? v.error :
        typeof v === 'number' ? formatCellNumber(v) :
        String(v)
    }
  }
  return { values, display, nRows, nCols }
}

export function formatCellNumber(v) {
  if (Number.isInteger(v)) return v.toLocaleString('en-IN')
  const fixed = Math.abs(v) >= 1 ? v.toFixed(2) : v.toPrecision(4)
  return Number(fixed).toLocaleString('en-IN', { maximumFractionDigits: 6 })
}
